import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel,
    IconButton, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import GavelIcon from '@mui/icons-material/Gavel';
import ReplayIcon from '@mui/icons-material/Replay';
import { differenceInCalendarDays } from 'date-fns';
import { SampleRequestService } from '../../services/sampleRequest.service';
import { BuyerService } from '../../services/buyer.service';
import { StyleService } from '../../services/style.service';
import { AuthService } from '../../services/auth.service';
import { ISampleRequest, SampleStatus, SampleType } from '../../interfaces/garments/sampleRequest.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { IStyle } from '../../interfaces/garments/style.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { ALL_SAMPLE_STATUSES, EDITABLE_SAMPLE_STATUSES, legalDecisionTargets, legalStatusTargets } from './sampleRequestTransitions';
import { money } from '../statutoryRegisters/shared';
import { BuyerAutocomplete } from './BuyerAutocomplete';
import { StyleAutocomplete } from './StyleAutocomplete';
import SampleRequestForm from './SampleRequestForm';
import SampleStatusDialog from './SampleStatusDialog';
import SampleDecisionDialog from './SampleDecisionDialog';
import { SAMPLE_TYPES } from './sampleTypeOptions';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';

const STATUS_COLOR: Record<SampleStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    REQUESTED: 'default',
    IN_WORK: 'info',
    SENT: 'primary',
    APPROVED: 'success',
    REJECTED: 'error',
    RESUBMIT_REQUIRED: 'warning',
    RESUBMITTED: 'default',
    CANCELLED: 'default',
};

/**
 * The merchandiser's daily sample-request screen (garments-merchandising T9, spec
 * `.superpowers/sdd/2026-09-09-garments-merchandising/t9-brief.md`). Filters by buyer/style/type/
 * status plus an overdue toggle; every row's Change Status / Buyer Decision / Resubmit actions are
 * gated to exactly the transitions `sampleRequestTransitions.ts` (mirroring the backend's
 * `SampleRequestService`) allows from that row's current status.
 */
export function SampleRequestList() {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.SAMPLE_REQUEST_MANAGE);

    const [rows, setRows] = useState<ISampleRequest[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);

    const [buyerFilter, setBuyerFilter] = useState<IBuyer | null>(null);
    const [styleFilter, setStyleFilter] = useState<IStyle | null>(null);
    const [typeFilter, setTypeFilter] = useState<SampleType | ''>('');
    const [statusFilter, setStatusFilter] = useState<SampleStatus | ''>('');
    const [overdueOnly, setOverdueOnly] = useState(false);

    // Id -> label lookups for the Buyer/Style columns — the sample request DTO carries only ids.
    const [buyerLabels, setBuyerLabels] = useState<Record<string, string>>({});
    const [styleLabels, setStyleLabels] = useState<Record<string, string>>({});

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<ISampleRequest | null>(null);
    const [statusTarget, setStatusTarget] = useState<ISampleRequest | null>(null);
    const [decisionTarget, setDecisionTarget] = useState<ISampleRequest | null>(null);
    const [resubmittingId, setResubmittingId] = useState<string | null>(null);

    useEffect(() => {
        loadLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, buyerFilter, styleFilter, typeFilter, statusFilter, overdueOnly]);

    const loadLookups = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        try {
            const [buyers, styles] = await Promise.all([
                BuyerService.list(institutionId, 0, 200),
                StyleService.list(institutionId, 0, 200),
            ]);
            setBuyerLabels(Object.fromEntries(buyers.content.map(b => [b.id, `${b.name} (${b.code})`])));
            setStyleLabels(Object.fromEntries(styles.content.map(s => [s.id, s.styleNumber])));
        } catch (e) {
            // Non-fatal — the table falls back to short ids when a lookup is missing.
        }
    };

    const load = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        setLoading(true);
        try {
            const res = await SampleRequestService.list(
                institutionId, page, size,
                buyerFilter?.id, styleFilter?.id,
                statusFilter || undefined, typeFilter || undefined, overdueOnly
            );
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load sample requests');
        } finally {
            setLoading(false);
        }
    };

    const buyerLabel = (id: string) => buyerLabels[id] ?? `${id.slice(0, 8)}…`;
    const styleLabel = (id: string) => styleLabels[id] ?? `${id.slice(0, 8)}…`;

    const age = (r: ISampleRequest) => {
        const anchor = r.decisionDate ?? r.sentDate ?? r.requestedDate;
        return Math.max(0, differenceInCalendarDays(new Date(), new Date(anchor)));
    };

    const handleSaved = (saved: ISampleRequest) => {
        setRows(prev => {
            const idx = prev.findIndex(r => r.id === saved.id);
            if (idx === -1) return [saved, ...prev];
            const next = [...prev];
            next[idx] = saved;
            return next;
        });
        load();
    };

    const handleResubmit = async (row: ISampleRequest) => {
        setResubmittingId(row.id);
        try {
            const child = await SampleRequestService.resubmit(AuthService.getSelectedInstitution(), row.id);
            toast.showSuccess(`Round ${child.roundNo} created`);
            // Await the refetch before clearing resubmittingId — once it resolves, this row's
            // status is RESUBMITTED, so canResubmit goes false and the button disappears entirely
            // rather than the disabled state fireEvent-flickering re-enabled in between.
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to resubmit');
        } finally {
            setResubmittingId(null);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (row: ISampleRequest) => {
        setEditing(row);
        setFormOpen(true);
    };

    const clearFilters = () => {
        setBuyerFilter(null);
        setStyleFilter(null);
        setTypeFilter('');
        setStatusFilter('');
        setOverdueOnly(false);
        setPage(0);
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.samples.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Sample Requests</Typography>
                {canManage && (
                    <Button variant="contained" startIcon={<AddIcon />} data-help="merchandising.samples.new" onClick={openCreate}>
                        New Sample Request
                    </Button>
                )}
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.samples.filter">
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <BuyerAutocomplete
                            value={buyerFilter}
                            onChange={b => { setBuyerFilter(b); setStyleFilter(null); setPage(0); }}
                            label="Buyer" size="small"
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <StyleAutocomplete
                            value={styleFilter}
                            onChange={s => { setStyleFilter(s); setPage(0); }}
                            label="Style" size="small" buyerId={buyerFilter?.id}
                        />
                    </Box>
                    <FormControl data-help="merchandising.samples.filterType" size="small" sx={{ flex: '1 1 150px', maxWidth: 270 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            label="Type" value={typeFilter}
                            onChange={e => { setTypeFilter(e.target.value as SampleType | ''); setPage(0); }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {SAMPLE_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl data-help="merchandising.samples.filterStatus" size="small" sx={{ flex: '1 1 170px', maxWidth: 290 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status" value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value as SampleStatus | ''); setPage(0); }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {ALL_SAMPLE_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        data-help="merchandising.samples.overdueOnly"
                        control={
                            <Checkbox
                                checked={overdueOnly}
                                onChange={e => { setOverdueOnly(e.target.checked); setPage(0); }}
                                inputProps={{ 'aria-label': 'Overdue only' }}
                            />
                        }
                        label="Overdue only"
                    />
                    <Button data-help="merchandising.samples.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Stack>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-help-section="merchandising.samples.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.samples.col.buyer">Buyer</TableCell>
                                <TableCell data-help="merchandising.samples.col.style">Style</TableCell>
                                <TableCell data-help="merchandising.samples.col.agent">Agent</TableCell>
                                <TableCell data-help="merchandising.samples.col.type">Type</TableCell>
                                <TableCell data-help="merchandising.samples.col.round">Round</TableCell>
                                <TableCell data-help="merchandising.samples.col.status">Status</TableCell>
                                <TableCell data-help="merchandising.samples.col.due">Due Date</TableCell>
                                <TableCell align="right" data-help="merchandising.samples.col.age">Age (d)</TableCell>
                                <TableCell align="right" data-help="merchandising.samples.col.cost">Cost</TableCell>
                                <TableCell align="right" data-help="merchandising.samples.col.hours">Hours</TableCell>
                                <TableCell align="right" data-help="merchandising.samples.col.actions">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => {
                                const statusOptions = legalStatusTargets(row.status);
                                const decisionOptions = legalDecisionTargets(row.status);
                                const editable = EDITABLE_SAMPLE_STATUSES.includes(row.status);
                                const canResubmit = row.status === 'RESUBMIT_REQUIRED';
                                return (
                                    <TableRow key={row.id} hover data-testid={`sample-row-${row.id}`}>
                                        <TableCell>{buyerLabel(row.buyerId)}</TableCell>
                                        <TableCell>{styleLabel(row.styleId)}</TableCell>
                                        <TableCell>
                                            {row.effectiveAgentPartyName ?? '—'}
                                            {row.agentSource === 'BUYER' && (
                                                <Chip size="small" label="Buyer default" sx={{ ml: 0.5 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>{enumLabel(row.sampleType)}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">Round {row.roundNo}</Typography>
                                            {row.parentSampleRequestId && (
                                                <Typography variant="caption" color="text.secondary">
                                                    ↳ resubmit of Round {row.roundNo - 1}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Chip data-help="merchandising.samples.status" size="small" label={enumLabel(row.status)} color={STATUS_COLOR[row.status]} />
                                                {row.overdue && <Chip data-help="merchandising.samples.overdue" size="small" label="Overdue" color="error" variant="outlined" />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{row.dueDate ? formatDate(row.dueDate) : '—'}</TableCell>
                                        <TableCell align="right">{age(row)}</TableCell>
                                        <TableCell align="right">{money(row.sampleCost)}</TableCell>
                                        <TableCell align="right">{row.laborHours ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                {canManage && editable && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" data-help="merchandising.samples.edit" onClick={() => openEdit(row)} aria-label="Edit">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canManage && statusOptions.length > 0 && (
                                                    <Tooltip title="Change Status">
                                                        <IconButton size="small" data-help="merchandising.samples.changeStatus" onClick={() => setStatusTarget(row)} aria-label="Change Status">
                                                            <SyncAltIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canManage && decisionOptions.length > 0 && (
                                                    <Tooltip title="Buyer Decision">
                                                        <IconButton size="small" data-help="merchandising.samples.decision" onClick={() => setDecisionTarget(row)} aria-label="Buyer Decision">
                                                            <GavelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canManage && canResubmit && (
                                                    <Tooltip title="Resubmit">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                aria-label="Resubmit" data-help="merchandising.samples.resubmit"
                                                                onClick={() => handleResubmit(row)}
                                                                disabled={resubmittingId === row.id}
                                                            >
                                                                <ReplayIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={11} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No sample requests match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}

            {formOpen && (
                <SampleRequestForm
                    open={formOpen}
                    sampleRequest={editing}
                    onClose={() => setFormOpen(false)}
                    onSaved={handleSaved}
                />
            )}
            {statusTarget && (
                <SampleStatusDialog
                    open={!!statusTarget}
                    sampleRequest={statusTarget}
                    onClose={() => setStatusTarget(null)}
                    onUpdated={handleSaved}
                />
            )}
            {decisionTarget && (
                <SampleDecisionDialog
                    open={!!decisionTarget}
                    sampleRequest={decisionTarget}
                    onClose={() => setDecisionTarget(null)}
                    onUpdated={handleSaved}
                />
            )}
        </Box>
    );
}

export default SampleRequestList;
